import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'

import useSession from '@/dealers/hooks/useSession'
import Action from '@/gf/components/Action'
import RadioInput from '@/gf/components/next/forms/RadioInput'
import DurationInput, {
  defaultDurationDates,
  defaultDurationId,
} from '@/gf/components/Reports/DurationInput'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/gf/components/ui/dialog'

type Include = 'all' | 'converted' | 'not_converted'
type Detail = 'line' | 'quote'

const includeOptions: { value: Include; label: string }[] = [
  { value: 'all', label: 'All quotes' },
  { value: 'converted', label: 'Converted only' },
  { value: 'not_converted', label: 'Not converted only' },
]

const detailOptions: { value: Detail; label: string }[] = [
  { value: 'line', label: 'One row per part' },
  { value: 'quote', label: 'One row per quote' },
]

const ExportOrdersModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const storeId = useSession().store.id

  const [durationId, setDurationId] = useState(defaultDurationId)
  const [start, setStart] = useState<DateTime>(defaultDurationDates.start)
  const [end, setEnd] = useState<DateTime>(defaultDurationDates.end)
  const [include, setInclude] = useState<Include>('all')
  const [detail, setDetail] = useState<Detail>('line')

  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestRef = useRef<AbortController | null>(null)

  // The modal stays mounted between openings, so reopening it would otherwise
  // inherit the last attempt's spinner and error. Closing drops any in-flight
  // request — this covers the parent closing it, not just the Cancel button.
  useEffect(() => {
    if (open) {
      setError(null)
      setDownloading(false)
      return
    }

    requestRef.current?.abort()
    requestRef.current = null
  }, [open])

  // Switching Reports tabs unmounts the modal outright, which no `open` change
  // would catch.
  useEffect(() => () => requestRef.current?.abort(), [])

  // Cancel stays enabled while a download is in flight and aborts it, so a
  // slow or stalled export can always be backed out of.
  const handleClose = () => {
    requestRef.current?.abort()
    requestRef.current = null
    onClose()
  }

  const filenameFrom = (disposition: string | null, fallback: string) =>
    disposition?.match(/filename="([^"]+)"/)?.[1] ?? fallback

  // Fetched rather than opened in a new tab: the server answers an empty range
  // with a 404 and a message worth reading, and a new tab would show it as raw
  // JSON with the modal already gone.
  const handleDownload = async () => {
    const startDate = start.toISODate() as string
    const endDate = end.toISODate() as string

    const params = new URLSearchParams({
      store_id: storeId,
      start_date: startDate,
      end_date: endDate,
      include,
      detail,
    })

    const request = new AbortController()
    requestRef.current = request

    setError(null)
    setDownloading(true)

    try {
      const response = await fetch(`/csv/download/orders?${params}`, {
        signal: request.signal,
      })

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        setError(body?.error?.title ?? 'Something went wrong preparing your download.')
        return
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = objectUrl
      link.download = filenameFrom(
        response.headers.get('content-disposition'),
        `orders_${startDate}_to_${endDate}.csv`
      )

      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)

      onClose()
    } catch (e) {
      // an abort is the user backing out, not a failure to report
      if ((e as Error)?.name !== 'AbortError') {
        setError('Something went wrong preparing your download.')
      }
    } finally {
      if (requestRef.current === request) requestRef.current = null
      setDownloading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Download Orders</DialogTitle>
          <DialogDescription className="text-base pt-2">
            Download a CSV of your quotes and orders from the selected time period.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-6">
          <div className="space-y-3">
            <label className="font-medium text-base">Date range</label>

            <DurationInput
              start={start}
              end={end}
              durationId={durationId}
              onChange={(dates) => {
                setDurationId(dates.durationId)
                setStart(dates.start)
                setEnd(dates.end)
              }}
              showDates
            />
          </div>

          <div className="space-y-3">
            <label className="font-medium text-base">Include</label>

            <div className="flex gap-x-6">
              {includeOptions.map((option) => (
                <label key={option.value} className="flex gap-x-2 items-center">
                  <RadioInput
                    checked={include === option.value}
                    onChange={() => setInclude(option.value)}
                    name="include"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="font-medium text-base">Detail</label>

            <div className="flex gap-x-6">
              {detailOptions.map((option) => (
                <label key={option.value} className="flex gap-x-2 items-center">
                  <RadioInput
                    checked={detail === option.value}
                    onChange={() => setDetail(option.value)}
                    name="detail"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          >
            {error}
          </p>
        )}

        <DialogFooter className="flex flex-row-reverse sm:flex-row sm:justify-end gap-3 sm:gap-2 mt-6">
          <Action.S onClick={handleClose}>Cancel</Action.S>
          <Action.P onClick={handleDownload} disabled={downloading} performing={downloading}>
            Download
          </Action.P>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ExportOrdersModal
